import { motion } from 'framer-motion';
import React, { useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    TextField,
    Button,
    Paper,
    useTheme,
    alpha,
    InputAdornment,
    Alert,
    IconButton
} from '@mui/material';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Person as PersonIcon,
    Subject as SubjectIcon,
    Message as MessageIcon,
    Send as SendIcon,
    Close as CloseIcon
} from '@mui/icons-material';

const Contact = () => {
    const theme = useTheme();
    const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.message.trim()) newErrors.message = 'Message is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setSubmitStatus('success');
            setFormData({ name: '', email: '', subject: '', message: '' });
            setTimeout(() => setSubmitStatus(null), 5000);
        } else setSubmitStatus('error');
    };

    const contactDetails = [
        { icon: <EmailIcon sx={{ fontSize: 30, color: 'white' }} />, title: 'Email', content: 'easysoftwarenepal@gmail.com', link: 'mailto:easysoftwarenepal@gmail.com' },
        { icon: <PhoneIcon sx={{ fontSize: 30, color: 'white' }} />, title: 'Phone', content: '+977-1-5165236', link: 'tel:+977-1-5165236' },
        { icon: <LocationIcon sx={{ fontSize: 30, color: 'white' }} />, title: 'Location', content: 'View on Google Maps', link: 'https://www.google.com/maps/place/Easy+Software+Pvt.+Ltd.' }
    ];

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const cardVariant = {
        hidden: { opacity: 0, x: -50 },
        visible: i => ({ opacity: 1, x: 0, transition: { delay: i * 0.2, duration: 0.6 } })
    };

    return (
        <Box sx={{
            py: { xs: 8, md: 12 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', top: '-10%', right: '-10%', width: '300px', height: '300px', borderRadius: '50%', background: `radial-gradient(${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`, zIndex: 0 },
            '&::after': { content: '""', position: 'absolute', bottom: '-10%', left: '-10%', width: '250px', height: '250px', borderRadius: '50%', background: `radial-gradient(${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`, zIndex: 0 }
        }}>
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={6} alignItems="center">
                    {/* Contact Form */}
                    <Grid item xs={12} md={6} lg={5}>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                            <Paper elevation={8} sx={{
                                p: 4,
                                borderRadius: 3,
                                background: `linear-gradient(to bottom right, white, ${alpha(theme.palette.primary.light, 0.1)})`,
                                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})` }
                            }}>
                                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.dark }}>
                                    Get In Touch
                                </Typography>

                                {submitStatus === 'success' && <Alert severity="success" sx={{ mb: 3, bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.dark, '& .MuiAlert-icon': { color: theme.palette.success.main } }}
                                    action={<IconButton size="small" onClick={() => setSubmitStatus(null)}><CloseIcon fontSize="small" /></IconButton>}>
                                    Message sent successfully! We'll get back to you soon.
                                </Alert>}

                                {submitStatus === 'error' && <Alert severity="error" sx={{ mb: 3, bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.dark, '& .MuiAlert-icon': { color: theme.palette.error.main } }}
                                    action={<IconButton size="small" onClick={() => setSubmitStatus(null)}><CloseIcon fontSize="small" /></IconButton>}>
                                    Please fix the errors in the form.
                                </Alert>}

                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Your Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment> }} sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Your Email" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment> }} sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Subject" name="subject" value={formData.subject} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><SubjectIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment> }} sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Message" name="message" multiline rows={4} value={formData.message} onChange={handleChange} error={!!errors.message} helperText={errors.message} InputProps={{ startAdornment: <InputAdornment position="start"><MessageIcon sx={{ color: theme.palette.primary.main, mt: 0.5, alignSelf: 'flex-start' }} /></InputAdornment> }} sx={{ mb: 3 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button type="submit" variant="contained" size="large" fullWidth startIcon={<SendIcon />} sx={{ py: 1.5, borderRadius: 2, fontWeight: 'bold', background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
                                                Send Message
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Paper>
                        </motion.div>
                    </Grid>

                    {/* Contact Details */}
                    <Grid item xs={12} md={6} lg={7}>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                            <Box sx={{ pl: { md: 4 } }}>
                                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.dark, position: 'relative', display: 'inline-block', '&::after': { content: '""', position: 'absolute', bottom: -8, left: 0, width: '60px', height: '4px', borderRadius: 2, background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})` } }}>
                                    Contact Details
                                </Typography>

                                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
                                    Start working with <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>esPay</Box> that can provide everything you need to generate awareness, drive traffic, and connect.
                                </Typography>

                                {contactDetails.map((detail, index) => (
                                    <motion.div key={index} custom={index} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariant}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, borderRadius: 2, background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                                            <Box sx={{ mr: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 60, height: 60, borderRadius: '50%', background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
                                                {detail.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, color: theme.palette.primary.dark }}>
                                                    {detail.title}
                                                </Typography>
                                                <Button href={detail.link} target={detail.title === 'Location' ? '_blank' : '_self'} sx={{ color: theme.palette.primary.main, textTransform: 'none', p: 0, fontWeight: 'medium', '&:hover': { color: theme.palette.primary.dark, textDecoration: 'underline' } }}>
                                                    {detail.content}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </motion.div>
                                ))}
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Contact;
